import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { PointExpiryPeriod } from '../schemas/point-expiry';
import { ensureID, headers, RecordOrID } from './meta';
import { invalidatePointExpiryForUsers } from './point-expiry-user';

enum QueryKey {
	Periods = 'point-expiry:periods',
	ImpactPreview = 'point-expiry:impact-preview',
}

export const pointExpiryPeriodsQueryKey = () => [QueryKey.Periods];
export const pointExpiryPeriodQueryKey = (id: string) => [...pointExpiryPeriodsQueryKey(), id];
export const pointExpiryImpactPreviewQueryKey = (startDate: string | null, endDate: string | null) => [QueryKey.ImpactPreview, startDate, endDate];

// The v2 http client already prefixes `/api/v2`, so path builders omit it.
export const pointExpiryPeriodsPath = (prefix = '') => `${prefix}/admin/point_expiry_periods`;
export const pointExpiryPeriodPath = (id: string, prefix = '') => `${prefix}/admin/point_expiry_periods/${id}`;
export const pointExpiryImpactPreviewPath = (prefix = '') => `${prefix}/admin/point_expiry_periods/impact_preview`;

// The backend index envelope. Keys arrive camelized via OliveBranch because the
// request carries the `Key-Inflection: camel` header.
type IndexResponse = { records: unknown[] };

const fetchPointExpiryPeriods = async (http: HttpClient): Promise<PointExpiryPeriod[]> => {
	const res = await http.get<IndexResponse>(pointExpiryPeriodsPath(), { headers });
	// Per-record safeParse so one malformed period can't blank the whole admin view.
	return res.data.records.flatMap((record) => {
		const parsed = PointExpiryPeriod.safeParse(record);
		return parsed.success ? [parsed.data] : [];
	});
};

export const usePointExpiryPeriods = (options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery<PointExpiryPeriod[]>({
		queryKey: pointExpiryPeriodsQueryKey(),
		queryFn: () => fetchPointExpiryPeriods(http),
		enabled: options?.enabled,
	});
};

const createPeriodFetcher = (http: HttpClient, id: string) => async (): Promise<PointExpiryPeriod> => {
	const res = await http.get<unknown>(pointExpiryPeriodPath(id), { headers });
	return PointExpiryPeriod.parse(res.data);
};

export const usePointExpiryPeriod = (id: string) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: pointExpiryPeriodQueryKey(id),
		queryFn: createPeriodFetcher(http, id),
		enabled: !!id,
	});
};

export const loadPointExpiryPeriod = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: pointExpiryPeriodQueryKey(id),
		queryFn: createPeriodFetcher(http, id),
	});
};

// Response of the impact_preview collection endpoint (camelized by OliveBranch via the
// `Key-Inflection: camel` header). Only the count is returned — no period is persisted.
const ImpactPreviewResponse = z.object({ impactedPointsCount: z.number() });

// Live company-wide impact count for ARBITRARY dates, without saving a period. Powers the
// admin form's dynamic "points that will expire" preview as the dates change. `startDate` is
// optional (blank → count all reward points up to the expiry date). The query only runs once an
// `endDate` is present, and keepPreviousData holds the last count on screen while refetching.
export const usePointExpiryImpactPreview = ({ startDate, endDate }: { startDate?: string | null; endDate?: string | null }) => {
	const http = useHttpClient();
	const start = startDate || null;
	const end = endDate || null;

	return useQuery<number>({
		queryKey: pointExpiryImpactPreviewQueryKey(start, end),
		queryFn: async () => {
			// end is guaranteed present here (query is disabled otherwise); start is sent only when set.
			const params: Record<string, string> = { end_at: end as string };
			if (start) params.start_at = start;

			const res = await http.get<unknown>(pointExpiryImpactPreviewPath(), { headers, params });
			return ImpactPreviewResponse.parse(res.data).impactedPointsCount;
		},
		enabled: !!end,
		keepPreviousData: true,
	});
};

// Mutation payload for create/update. Dates are `YYYY-MM-DD` strings; `startDate`
// is nullable (blank → the period begins immediately).
export type PointExpiryPeriodMutationPayload = {
	startDate: string;
	endDate: string;
	warningDate: string;
};

type MutateOptions<R> = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (record: R) => void;
};

const buildBody = (payload: PointExpiryPeriodMutationPayload): Record<string, unknown> => ({
	// `start_at` is nullable — send null when blank so the backend starts the period now.
	start_at: payload.startDate || null,
	end_at: payload.endDate,
	warning_at: payload.warningDate,
});

export const useCreatePointExpiryPeriod = (options: MutateOptions<PointExpiryPeriod> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidatePointExpiryPeriods();

	return useMutation({
		mutationFn: async (payload: PointExpiryPeriodMutationPayload) => {
			try {
				options.onMutate?.();
				const res = await http.post<unknown>(pointExpiryPeriodsPath(), buildBody(payload), { headers });
				const record = PointExpiryPeriod.parse(res.data);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useUpdatePointExpiryPeriod = (recordOrId: RecordOrID, options: MutateOptions<PointExpiryPeriod> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidatePointExpiryPeriods();

	return useMutation({
		mutationFn: async (payload: PointExpiryPeriodMutationPayload) => {
			try {
				options.onMutate?.();
				const res = await http.put<unknown>(pointExpiryPeriodPath(ensureID(recordOrId)), buildBody(payload), { headers });
				const record = PointExpiryPeriod.parse(res.data);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useDeletePointExpiryPeriod = (options: MutateOptions<void> = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidatePointExpiryPeriods();

	return useMutation({
		mutationFn: async (recordOrId: RecordOrID) => {
			await http.delete(pointExpiryPeriodPath(ensureID(recordOrId)), { headers });
		},
		onSuccess: () => {
			invalidate();
			options.onSuccess?.(undefined as void);
		},
		onError: (e) => {
			options.onError?.(e);
		},
	});
};

export const useInvalidatePointExpiryPeriods = () => {
	const client = useQueryClient();
	return useCallback(() => {
		client.invalidateQueries(pointExpiryPeriodsQueryKey());
		// Creating, editing or cancelling a period changes the date and count every user's profile
		// line reports, so the per-user summaries this session has cached are now wrong too.
		invalidatePointExpiryForUsers(client);
	}, [client]);
};
